
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Bell, Calendar, Users, Trophy, MessageCircle, AlertTriangle, CheckCheck, Filter } from 'lucide-react';
import { useNotifications } from '@/hooks/use-notifications';
import { useLanguage } from '@/hooks/use-language';
import { useNotificationClick } from '@/hooks/use-notification-click';
import { NotificationFilter } from '@/types/notification';

export default function Notifications() {
  const { handleNotificationClick } = useNotificationClick();
  const { t } = useLanguage();
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead, 
    getFilteredNotifications,
    isLoading 
  } = useNotifications();
  
  const [activeFilter, setActiveFilter] = useState<NotificationFilter>('all');

  const getIcon = (type: string) => {
    switch (type) {
      case 'event':
        return Calendar;
      case 'tournament':
        return Trophy;
      case 'group':
        return Users;
      case 'message':
        return MessageCircle;
      case 'penalty':
        return AlertTriangle;
      default:
        return Bell;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'event':
        return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      case 'tournament':
        return 'bg-green-500/10 text-green-600 border-green-500/20';
      case 'group':
        return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
      case 'message':
        return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
      case 'penalty':
        return 'bg-red-500/10 text-red-600 border-red-500/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-500';
      case 'medium':
        return 'bg-orange-500';
      case 'low':
        return 'bg-blue-500';
      default:
        return 'bg-muted-foreground';
    }
  };

  const formatTimeAgo = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
      return t("notifications.time.minutes", { count: minutes });
    } else if (hours < 24) {
      return t("notifications.time.hours", { count: hours });
    } else {
      return t("notifications.time.days", { count: days });
    }
  };

  const onNotificationClick = (notification: any) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    handleNotificationClick(notification);
  };

  const filteredNotifications = getFilteredNotifications(activeFilter);

  if (isLoading) {
    return (
      <div className="space-y-4 px-1">
        <div className="px-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t("notifications.title")}</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">{t("notifications.loading")}</p>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse mx-1">
              <CardHeader className="pb-2">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-muted rounded-full flex-shrink-0"></div>
                  <div className="flex-1 space-y-2 min-w-0">
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 px-1">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-1">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground truncate">{t("notifications.title")}</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            {t("notifications.subtitle")} {unreadCount > 0 && t("notifications.unread_count", { count: unreadCount })}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button onClick={markAllAsRead} variant="outline" size="sm" className="self-start sm:self-auto flex-shrink-0">
            <CheckCheck className="h-4 w-4 mr-2" />
            <span className="hidden xs:inline">{t("notifications.mark_all_read")}</span>
            <span className="xs:hidden">Mark all</span>
          </Button>
        )}
      </div>

      <Tabs value={activeFilter} onValueChange={(value) => setActiveFilter(value as NotificationFilter)} className="w-full">
        <div className="px-1">
          <TabsList className="grid w-full grid-cols-3 sm:grid-cols-6 h-auto p-1">
            <TabsTrigger value="all" className="text-xs sm:text-sm px-2 py-2">{t("notifications.filter.all")}</TabsTrigger>
            <TabsTrigger value="unread" className="text-xs sm:text-sm px-2 py-2">{t("notifications.filter.unread")}</TabsTrigger>
            <TabsTrigger value="event" className="text-xs sm:text-sm px-2 py-2 col-span-1 sm:col-span-1">{t("notifications.filter.event")}</TabsTrigger>
            <TabsTrigger value="tournament" className="text-xs sm:text-sm px-2 py-2 col-span-1 sm:col-span-1">{t("notifications.filter.tournament")}</TabsTrigger>
            <TabsTrigger value="message" className="text-xs sm:text-sm px-2 py-2 col-span-1 sm:col-span-1">{t("notifications.filter.message")}</TabsTrigger>
            <TabsTrigger value="penalty" className="text-xs sm:text-sm px-2 py-2 col-span-1 sm:col-span-1">{t("notifications.filter.penalty")}</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value={activeFilter} className="space-y-3 mt-4 sm:mt-6 px-1">
          {filteredNotifications.length === 0 ? (
            <Card className="mx-1">
              <CardContent className="flex flex-col items-center justify-center py-8 sm:py-12 px-4">
                <Bell className="h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground mb-3 sm:mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-foreground mb-2 text-center">{t("notifications.empty.title")}</h3>
                <p className="text-muted-foreground text-center text-sm sm:text-base max-w-sm">
                  {activeFilter === 'all' 
                    ? t("notifications.empty.description")
                    : t("notifications.empty.filtered", { filter: activeFilter === 'unread' ? t("notifications.filter.unread").toLowerCase() : t(`notifications.filter.${activeFilter}`).toLowerCase() })
                  }
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredNotifications.map((notification) => {
              const IconComponent = getIcon(notification.type);
              
              return (
                <Card 
                  key={notification.id} 
                  className={`transition-all hover:shadow-md cursor-pointer mx-1 ${
                    !notification.isRead ? 'border-l-4 border-l-primary bg-muted/20' : ''
                  }`}
                  onClick={() => onNotificationClick(notification)}
                >
                  <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6 pt-3 sm:pt-6">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`p-2 rounded-full border flex-shrink-0 ${getTypeColor(notification.type)}`}>
                          <IconComponent className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2 mb-1 flex-wrap">
                            <CardTitle className="text-sm sm:text-base font-medium break-words">
                              {notification.title}
                            </CardTitle>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {!notification.isRead && (
                                <Badge variant="secondary" className="text-xs px-1.5 py-0.5 whitespace-nowrap">
                                  {t("notifications.new_badge")}
                                </Badge>
                              )}
                              <div 
                                className={`w-2 h-2 rounded-full flex-shrink-0 ${getPriorityColor(notification.priority)}`}
                                title={`${notification.priority} priority`}
                              />
                            </div>
                          </div>
                          <CardDescription className="text-xs sm:text-sm break-words">
                            {notification.description}
                          </CardDescription>
                          {notification.actionUrl && (
                            <p className="text-xs text-primary mt-1">{t("notifications.click_details")}</p>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground flex-shrink-0 ml-2 mt-1">
                        {formatTimeAgo(notification.timestamp)}
                      </span>
                    </div>
                  </CardHeader>
                </Card>
              );
            })
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
